// EXEC named-parameter slot detector (Workstream C, mssql 37.§3-§5).
//
// Once the EXEC target is complete and the argument list has started, the
// caret expects the procedure's PARAMETER NAMES — `EXEC dbo.proc ▮`,
// `EXEC dbo.proc @Cat▮`, `EXEC dbo.proc @A = 1, ▮`. The clause machine has
// no EXEC arm, so these fell through to generic fallbacks. This detector
// identifies the slot AND extracts the procedure name so the resolver can
// look up its signature in the schema-bus routines.
//
// Deliberately does NOT fire on the VALUE side of an assignment
// (`EXEC dbo.proc @A = ▮` wants @locals / literals, not parameter names) —
// the after-last-comma tail must be only whitespace plus an optional
// in-progress `@token`.

import { maskStringsAndComments } from '@/features/workspace/common/utils/sqlTextMasker';

export type ExecNamedParamSlot = {
  readonly procName: string;
  readonly procSchema: string | null;
};

// Head = EXEC[UTE] + optional return-capture (`EXEC @rc = proc`) + the target
// name (schema-qualified, [bracketed], "quoted", # temp procs), then the rest
// of the statement up to the caret. `EXECUTE AS …` impersonation is excluded
// via the negative lookahead.
// Each name segment is `[bracketed]` (spaces allowed), `"quoted"`, or a bare
// word (## temp-proc prefixes allowed on the first segment only).
const EXEC_HEAD_RE =
  /\bEXEC(?:UTE)?\s+(?:@[\w$]+\s*=\s*)?(?!AS\b)((?:\[[^\]]*\]|"[^"]*"|#{0,2}[A-Za-z_][\w$#]*)(?:\.(?:\[[^\]]*\]|"[^"]*"|[A-Za-z_][\w$#]*))*)([\s\S]*)$/i;

// Tail after the last depth-0 comma: whitespace + an optional in-progress
// parameter token. Anything else (an `=`, a literal, a closing paren) means
// the caret is mid-value, not at a parameter-name slot.
const PARAM_TAIL_RE = /^\s*@?[\w$]*$/;

export function isAtExecNamedParamSlot(
  text: string,
  offset: number,
  statementStart: number,
): ExecNamedParamSlot | null {
  // `EXEC` is T-SQL-only, so the masker gets `mssql`: `[…]` and `"…"` are
  // identifier delimiters here (`EXEC [dbo].[my proc]`), while a backtick,
  // `$…$` and `q'…'` are ordinary characters.
  const slice = maskStringsAndComments(text.slice(statementStart, offset), { engine: 'mssql' });
  const m = EXEC_HEAD_RE.exec(slice);
  if (!m) return null;
  const rest = m[2];
  // Name still in progress (no separator yet) → that's the EXEC-target slot,
  // handled by isAtExecTargetSlot; the two detectors stay disjoint.
  if (!/^\s/.test(rest)) return null;
  const tail = rest.slice(lastTopLevelCommaIndex(rest) + 1);
  if (!PARAM_TAIL_RE.test(tail)) return null;
  const parts = splitQualifiedName(m[1]);
  const procName = parts[parts.length - 1] ?? '';
  if (!procName) return null;
  const procSchema = parts.length >= 2 && parts[parts.length - 2] !== ''
    ? parts[parts.length - 2]
    : null;
  return { procName, procSchema };
}

/**
 * Index of the last comma at paren depth 0, or -1. Commas inside expression
 * values (`@A = CONVERT(int, …)`) must not be mistaken for argument
 * separators. String commas are already space-masked by the caller.
 */
function lastTopLevelCommaIndex(rest: string): number {
  let depth = 0;
  let last = -1;
  for (let i = 0; i < rest.length; i++) {
    const ch = rest[i];
    if (ch === '(') depth++;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    else if (ch === ',' && depth === 0) last = i;
  }
  return last;
}

/** Split `db.schema.proc` on dots, de-bracketing `[…]` / `"…"` parts. */
function splitQualifiedName(qualified: string): string[] {
  const parts: string[] = [];
  let cur = '';
  let inBracket = false;
  let inQuote = false;
  for (const ch of qualified) {
    if (inBracket) {
      if (ch === ']') inBracket = false;
      else cur += ch;
      continue;
    }
    if (inQuote) {
      if (ch === '"') inQuote = false;
      else cur += ch;
      continue;
    }
    if (ch === '[') { inBracket = true; continue; }
    if (ch === '"') { inQuote = true; continue; }
    if (ch === '.') { parts.push(cur); cur = ''; continue; }
    cur += ch;
  }
  parts.push(cur);
  return parts;
}
